// MIDAS V3 — Artifact dock header (Phase 3)
//
// Tab strip + describe (i) button + dock close (X).
//
// Spec: MIDAS_V3_PHASE3_ARTIFACT_DOCK_SPEC.md §3
// Handoff: artifact-pane.jsx `ArtifactHeader` + `chat-with-artifact` tab idiom.
//
// Visual idiom — "tab sits on top of bottom border":
//   • Header background: var(--bg-soft) with bottom border
//   • Each tab pill: card-coloured, bordered, sitting ON TOP of the bottom
//     border via marginBottom: -1 so the active tab visually "merges" into
//     the dock body below it.
//   • Active tab: var(--card) bg + full border, hides the bottom hairline
//     beneath itself. Inactive tabs: faded bg + softer border.
//
// Logic (describe / close):
//   • The describe (i) button reuses v2's MC-26 / MC-27 pattern verbatim:
//     resolveInfoPrompt → synthesiseInfoPrompt fallback → buildFriendlyDisplayText
//     → dispatch sendMessage with displayText + pageInfoRequest. No new
//     endpoint. The same helpers are copied here (small, fully scoped) so the
//     v3 header has no v2 dependency.
//   • The dock close (X) dispatches `closeArtifactPanel` (existing v2 action).
//   • The per-tab × dispatches `closeArtifactTab` (existing v2 action).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useMemo } from "react";
// R5 sub-task 2c: swap Maximize2/Minimize2 → Expand/Shrink for the
// open-fullscreen toggle. The v2 diagram viewer's bottom-right reset-view
// button still uses Maximize2 (v2 byte-frozen, R5 sub-task 2a wraps but
// does not restyle), so two distinct icons disambiguate the two actions.
import { Eye, EyeOff, Expand, Shrink, X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import i18n from "../../../../../core/i18n";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { sendMessage } from "../../../../../shared/chatBot/chatBotSlice";
import { DiagramOverlayArtifactData } from "../../../../../shared/chatBot/models/chatBotModels";
import {
  closeArtifactPanel,
  closeArtifactTab,
  setActiveArtifactTab,
} from "../../../midasPageSlice";
import {
  setFullscreenArtifactId,
  setOverlayVisibility,
} from "../../../midasV3Slice";
import { ArtifactTab } from "../../../midasPageModels";

// ───────────────────────────────────────────────────────────────────
// i18n helpers — verbatim from v2 MidasArtifactHeaderV2 (MC-26 / MC-27).
// Local copy keeps v3 chrome independent of v2's header component.
// ───────────────────────────────────────────────────────────────────

/** MC-26: resolve localised info_prompt body with fallback chain
 * user language → sk → en → first available key */
const resolveInfoPrompt = (
  infoPrompt: { sk?: string; en?: string; cs?: string } | undefined,
  lang: string
): string | null => {
  if (!infoPrompt) return null;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  const primary = (infoPrompt as Record<string, string | undefined>)[langKey];
  if (primary && typeof primary === "string" && primary.length > 0) return primary;
  if (infoPrompt.sk && infoPrompt.sk.length > 0) return infoPrompt.sk;
  if (infoPrompt.en && infoPrompt.en.length > 0) return infoPrompt.en;
  if (infoPrompt.cs && infoPrompt.cs.length > 0) return infoPrompt.cs;
  for (const v of Object.values(infoPrompt)) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
};

const resolveLocalisedName = (
  data: DiagramOverlayArtifactData | undefined,
  lang: string
): string | undefined => {
  if (!data) return undefined;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en" && data.name_en) return data.name_en;
  if (langKey === "cs" && data.name_cs) return data.name_cs;
  if (data.name) return data.name;
  if (data.name_en) return data.name_en;
  if (data.name_cs) return data.name_cs;
  if (data.page_name) return data.page_name;
  return undefined;
};

const synthesiseInfoPrompt = (tab: ArtifactTab, lang: string): string | null => {
  if (tab.artifact.type !== "diagram_overlay") return null;
  const data = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = data?.page ?? tab.artifact.page;
  if (page === undefined || page === null) return null;
  const name = resolveLocalisedName(data, lang) ?? tab.artifact.title;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en") {
    return name
      ? `Explain page ${page} — ${name} from the electrical schema collection`
      : `Explain page ${page} from the electrical schema collection`;
  }
  if (langKey === "cs") {
    return name
      ? `Vysvětli mi stránku ${page} — ${name} ze sbírky elektrických schémat`
      : `Vysvětli mi stránku ${page} ze sbírky elektrických schémat`;
  }
  return name
    ? `Vysvetli mi stránku ${page} — ${name} zo zbierky elektrických schém`
    : `Vysvetli mi stránku ${page} zo zbierky elektrických schém`;
};

const buildFriendlyDisplayText = (tab: ArtifactTab, lang: string): string => {
  const data = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = data?.page ?? tab.artifact.page;
  const name = resolveLocalisedName(data, lang);
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en") {
    return name ? `Describe page ${page} — ${name}` : `Describe page ${page}`;
  }
  return name ? `Popis stránky ${page} — ${name}` : `Popis stránky ${page}`;
};

// ───────────────────────────────────────────────────────────────────
// TabPill — single tab in the strip
// ───────────────────────────────────────────────────────────────────

interface TabPillProps {
  tab: ArtifactTab;
  isActive: boolean;
  onActivate: () => void;
  onClose: (e: React.MouseEvent) => void;
}

const TabPill: React.FC<TabPillProps> = ({ tab, isActive, onActivate, onClose }) => {
  return (
    <div
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px 8px",
        background: isActive ? "var(--card)" : "var(--card-2)",
        border: `1px solid ${isActive ? "var(--border)" : "var(--border-soft)"}`,
        // Active tab "sits on" the bottom border: the bottom border colour
        // matches the card so the hairline beneath the active tab is hidden.
        borderBottom: `1px solid ${isActive ? "var(--card)" : "var(--border-soft)"}`,
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        marginBottom: -1,
        marginRight: 4,
        fontSize: 12,
        cursor: "pointer",
        maxWidth: 200,
        minWidth: 0,
        flexShrink: 1,
        transition: "background 0.12s ease",
        outline: "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          background: isActive ? "var(--accent)" : "var(--faint)",
          borderRadius: 1,
          flexShrink: 0,
        }}
      />
      <span
        className="mono"
        style={{
          color: isActive ? "var(--ink)" : "var(--ink-2)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          letterSpacing: 0,
          minWidth: 0,
          flex: 1,
        }}
      >
        {tab.artifact.title}
      </span>
      <button
        type="button"
        aria-label="Close tab"
        onClick={(e) => {
          e.stopPropagation();
          onClose(e);
        }}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          marginLeft: 2,
          color: "var(--faint)",
          fontSize: 12,
          cursor: "pointer",
          width: 14,
          height: 14,
          display: "inline-grid",
          placeItems: "center",
          borderRadius: 999,
          flexShrink: 0,
        }}
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────
// ArtifactHeaderV3
// ───────────────────────────────────────────────────────────────────

export interface ArtifactHeaderV3Props {
  /** When true, header is rendered inside the fullscreen overlay; suppresses
   *  the dock-level close X (fullscreen has its own back-button row). */
  fullscreen?: boolean;
}

const ArtifactHeaderV3: React.FC<ArtifactHeaderV3Props> = ({ fullscreen = false }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  const tabs = useAppSelector((s) => s.midasPage.artifactTabs);
  const activeTabId = useAppSelector((s) => s.midasPage.activeArtifactTabId);
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);

  // R2 sub-task 1: fullscreen toggle moved from ArtifactFooterV3 (footer
  // removed entirely) onto the header. Reads the same `fullscreenArtifactId`
  // slice field the footer was using; dispatch unchanged.
  const fullscreenArtifactId = useAppSelector((s) => s.midasV3.fullscreenArtifactId);
  const activeArtifactId = activeTab?.id ?? null;
  const isCurrentFullscreen =
    !!activeArtifactId && fullscreenArtifactId === activeArtifactId;

  // R3 sub-task 1: overlay-eye toggle restored from the now-deleted
  // ArtifactFooterV3 onto the header. Diagram-only — `activeTab.artifact.type`
  // gates render below. The v2 diagram viewer reads `overlayMap[tab.id]` via
  // `overlaysVisible` prop (additive change made in MIDAS-V3-P3); writing the
  // slice here propagates into the viewer's internal `effectiveShowOverlays`.
  // Default = visible (true) when the map has no entry.
  const isDiagramOverlay = activeTab?.artifact.type === "diagram_overlay";
  const overlaysVisible = useAppSelector((s) => {
    if (!activeArtifactId) return true;
    const stored = s.midasV3.overlayVisibility[activeArtifactId];
    return stored === undefined ? true : stored;
  });

  // MC-26: disable describe while any thread/global stream is active.
  const isStreamingGlobal = useAppSelector((state) => {
    if (state.chatBot.isLoading) return true;
    const buffers = state.chatBot.threadBuffers || {};
    for (const key of Object.keys(buffers)) {
      if (buffers[key]?.isLoading) return true;
    }
    return false;
  });

  const infoPromptBody = useMemo(() => {
    if (!activeTab) return null;
    const data = activeTab.artifact.data as DiagramOverlayArtifactData | undefined;
    const fromBot = resolveInfoPrompt(data?.info_prompt, i18n.language);
    if (fromBot) return fromBot;
    return synthesiseInfoPrompt(activeTab, i18n.language);
  }, [activeTab]);

  const handleActivate = useCallback(
    (tabId: string) => {
      dispatch(setActiveArtifactTab(tabId));
    },
    [dispatch]
  );

  const handleCloseTab = useCallback(
    (tabId: string) => {
      dispatch(closeArtifactTab(tabId));
    },
    [dispatch]
  );

  const handleClosePanel = useCallback(() => {
    dispatch(closeArtifactPanel());
  }, [dispatch]);

  const handleFullscreenToggle = useCallback(() => {
    if (!activeArtifactId) return;
    if (isCurrentFullscreen) {
      dispatch(setFullscreenArtifactId(null));
    } else {
      dispatch(setFullscreenArtifactId(activeArtifactId));
    }
  }, [activeArtifactId, isCurrentFullscreen, dispatch]);

  // R3 sub-task 1: overlay-eye toggle handler — mirrors ArtifactFooterV3's
  // pre-R2 handleEyeToggle verbatim. The v2 diagram viewer's internal
  // `Eye/EyeOff` button is a fallback ONLY when `overlaysVisible` is
  // undefined; once we dispatch here, the viewer reads the slice value via
  // its additive `overlaysVisible` prop (set by ArtifactContentV3 in
  // MidasArtifactPaneV3.tsx).
  const handleEyeToggle = useCallback(() => {
    if (!activeArtifactId) return;
    dispatch(
      setOverlayVisibility({
        artifactId: activeArtifactId,
        visible: !overlaysVisible,
      })
    );
  }, [activeArtifactId, overlaysVisible, dispatch]);

  const handleDescribe = useCallback(() => {
    if (!activeTab) return;
    if (isStreamingGlobal) return;
    const data = activeTab.artifact.data as DiagramOverlayArtifactData | undefined;
    const fromBot = resolveInfoPrompt(data?.info_prompt, i18n.language);
    const promptBody = fromBot ?? synthesiseInfoPrompt(activeTab, i18n.language);
    if (!promptBody) return;
    const friendly = buildFriendlyDisplayText(activeTab, i18n.language);

    // MC-27: structured page hint for diagram_overlay artifacts.
    let pageInfoRequest:
      | { page: number; collection: string; list_id?: string }
      | undefined;
    if (activeTab.artifact.type === "diagram_overlay") {
      const page = data?.page ?? activeTab.artifact.page;
      if (typeof page === "number" && Number.isFinite(page)) {
        pageInfoRequest = {
          page,
          collection: data?.collection ?? "sbl500_electrical_toc_v2",
          ...(data?.list_id ? { list_id: data.list_id } : {}),
        };
      }
    }

    dispatch(
      sendMessage({
        message: promptBody,
        displayText: friendly,
        machineId: machineContext?.machineId,
        threadId: activeThreadId || undefined,
        originThreadId: activeThreadId || undefined,
        pageInfoRequest,
      })
    );
  }, [activeTab, isStreamingGlobal, activeThreadId, machineContext, dispatch]);

  const describeAria = (translate("midas.v3.artifact.describeButton.tooltip", {
    defaultValue: "Describe this page — ask Midas to explain",
  }) as string);

  const closeAria = (translate("midas.v3.artifact.closeDockButton.label", {
    defaultValue: "Close artifact dock",
  }) as string);

  const fullscreenAria = isCurrentFullscreen
    ? (translate("midas.v3.artifact.exitFullscreenButton.tooltip", {
        defaultValue: "Exit fullscreen",
      }) as string)
    : (translate("midas.v3.artifact.fullscreenButton.tooltip", {
        defaultValue: "Open fullscreen",
      }) as string);

  // R3 sub-task 1: eye-toggle aria — same i18n keys the old footer used.
  const eyeAria = overlaysVisible
    ? (translate("midas.v3.artifact.hideOverlays", {
        defaultValue: "Hide overlays",
      }) as string)
    : (translate("midas.v3.artifact.showOverlays", {
        defaultValue: "Show overlays",
      }) as string);

  return (
    <div
      role="tablist"
      aria-label="Artifact tabs"
      style={{
        display: "flex",
        alignItems: "flex-end",
        padding: "10px 14px 0",
        gap: 0,
        borderBottom: "1px solid var(--border-soft)",
        background: "var(--bg-soft)",
        flexShrink: 0,
      }}
    >
      {/* Tab strip — wraps onto multiple lines is undesirable; clip overflow. */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {tabs.map((tab) => (
          <TabPill
            key={tab.id}
            tab={tab}
            isActive={tab.id === activeTabId}
            onActivate={() => handleActivate(tab.id)}
            onClose={() => handleCloseTab(tab.id)}
          />
        ))}
      </div>

      {/* Actions — drop down to align baseline with tab strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          paddingBottom: 8,
          flexShrink: 0,
        }}
      >
        {infoPromptBody && (
          <button
            type="button"
            onClick={handleDescribe}
            disabled={isStreamingGlobal}
            aria-label={describeAria}
            title={describeAria}
            style={{
              // R5 sub-task 2b: bump 28 → 32 for visual weight closer to the
              // user-avatar pill in the rail/header row. Describe button keeps
              // its 999 radius (circle by design — not a square ghost button).
              width: 32,
              height: 32,
              padding: 0,
              border: "none",
              background: "transparent",
              borderRadius: 999,
              cursor: isStreamingGlobal ? "not-allowed" : "pointer",
              display: "inline-grid",
              placeItems: "center",
              opacity: isStreamingGlobal ? 0.55 : 1,
              transition: "opacity 0.12s ease",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-grid",
                placeItems: "center",
                width: 18,
                height: 18,
                borderRadius: 999,
                border: "1.5px solid var(--accent)",
                color: "var(--accent)",
                fontFamily: "var(--font-sans)",
                fontWeight: 700,
                fontSize: 11,
                fontStyle: "italic",
                background: "var(--accent-soft)",
                lineHeight: 1,
              }}
            >
              i
            </span>
          </button>
        )}
        {/* R3 sub-task 1: eye-toggle restored from ArtifactFooterV3 (which
            R2 dropped entirely). Diagram_overlay artifacts only. Dispatches
            setOverlayVisibility — the v2 diagram viewer reads it via its
            additive `overlaysVisible` prop and toggles its internal hotspot
            overlays. The accent color when ON mirrors the old footer's
            visual; muted EyeOff when OFF. */}
        {!fullscreen && isDiagramOverlay && activeTab && (
          <button
            type="button"
            onClick={handleEyeToggle}
            aria-label={eyeAria}
            title={eyeAria}
            style={{
              // R5 sub-task 2b: 28 → 32, icon 15 → 16, radius 6 → 8.
              width: 32,
              height: 32,
              padding: 0,
              border: "none",
              background: "transparent",
              borderRadius: 8,
              cursor: "pointer",
              display: "inline-grid",
              placeItems: "center",
              color: overlaysVisible ? "var(--accent)" : "var(--muted)",
              transition: "color 0.12s ease, background 0.12s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--card-2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {overlaysVisible ? (
              <Eye size={16} strokeWidth={2} />
            ) : (
              <EyeOff size={16} strokeWidth={2} />
            )}
          </button>
        )}
        {/* R2 sub-task 1: fullscreen toggle moved here from the now-deleted
            ArtifactFooterV3. Same dispatch + same i18n keys; only the host
            element changed. */}
        {!fullscreen && activeTab && (
          <button
            type="button"
            onClick={handleFullscreenToggle}
            aria-label={fullscreenAria}
            title={fullscreenAria}
            style={{
              // R5 sub-task 2b: 28 → 32, icon 15 → 16, radius 6 → 8.
              width: 32,
              height: 32,
              padding: 0,
              border: "none",
              background: "transparent",
              borderRadius: 8,
              cursor: "pointer",
              display: "inline-grid",
              placeItems: "center",
              color: "var(--ink-2)",
              transition: "color 0.12s ease, background 0.12s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--card-2)";
              e.currentTarget.style.color = "var(--ink)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--ink-2)";
            }}
          >
            {/* R5 sub-task 2c: Expand/Shrink (four-corner arrows) replace
                Maximize2/Minimize2 (two-arrow corner-mirror) so the header
                open-fullscreen button is visually distinct from the v2
                diagram viewer's bottom-right reset-view button (still
                Maximize2, v2 byte-frozen). */}
            {isCurrentFullscreen ? (
              <Shrink size={16} strokeWidth={2} />
            ) : (
              <Expand size={16} strokeWidth={2} />
            )}
          </button>
        )}
        {!fullscreen && (
          <button
            type="button"
            onClick={handleClosePanel}
            aria-label={closeAria}
            title={closeAria}
            style={{
              // R5 sub-task 2b: 28 → 32, icon 16 → 17, radius 6 → 8.
              width: 32,
              height: 32,
              padding: 0,
              border: "none",
              background: "transparent",
              borderRadius: 8,
              cursor: "pointer",
              display: "inline-grid",
              placeItems: "center",
              color: "var(--ink-2)",
              transition: "color 0.12s ease",
            }}
          >
            <X size={17} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ArtifactHeaderV3;
